import React, { useState } from "react";
import { useField, useFormikContext } from "formik";
import DatePicker from "react-datepicker";
import FieldFeedback, { useFieldFeedback } from "./FieldFeedback";

const calendarParts = (date) => [date.getFullYear(), date.getMonth() + 1, date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds()];
const utcTime = ([year, month, day, hour = 0, minute = 0, second = 0]) => Date.UTC(year, month - 1, day, hour, minute, second);
const zonedParts = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
  }).formatToParts(date);
  return ["year", "month", "day", "hour", "minute", "second"].map(type => Number(parts.find(part => part.type === type).value));
};

// react-datepicker uses browser-local Date components. Adapt only explicitly regional fields;
// timestamps remain UTC on the wire, while calendar dates never acquire a timezone.
export const dateForPicker = (value, { timeZone, calendarDate } = {}) => {
  if (!value) return null;
  if (calendarDate && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day, 12);
  }
  const date = new Date(value);
  if (!timeZone || !Number.isFinite(date.getTime())) return date;
  const [year, month, day, hour, minute, second] = zonedParts(date, timeZone);
  return new Date(year, month - 1, day, hour, minute, second);
};

export const dateFromPicker = (date, { timeZone, calendarDate } = {}) => {
  if (date == null) return "";
  if (calendarDate) return calendarParts(date).slice(0, 3).map((value, index) => String(value).padStart(index === 0 ? 4 : 2, "0")).join("-");
  if (!timeZone) return String(date);
  const wallTime = utcTime(calendarParts(date));
  const offset = utcTime(zonedParts(new Date(wallTime), timeZone)) - wallTime;
  return new Date(wallTime - offset).toISOString();
};

const AADatepicker = ({ label, hint, dateFormat = "dd.MM.yyyy", timeZone, calendarDate = false, ...props }) => {
  const [field, meta] = useField(props);
  const { setFieldValue } = useFormikContext();
  const [draft, setDraft] = useState(null);
  const date = dateForPicker(field.value, { timeZone, calendarDate });
  const invalid = date !== null && !Number.isFinite(date.getTime());
  const error = meta.error || (invalid ? "Дата не распознана. Исправьте значение или выберите дату в календаре." : undefined);
  const feedback = useFieldFeedback(props, error, hint);

  return (
    <li data-signal-field="" data-signal-label={label} data-signal-name={props.name}>
      <label htmlFor={feedback.id} className="custom_label">{label}</label>

      <DatePicker
        {...props}
        {...feedback}
        customInput={React.cloneElement(props.customInput || <input type="text" />, {
          "aria-invalid": feedback["aria-invalid"],
          "aria-describedby": feedback["aria-describedby"],
        })}
        dateFormat={dateFormat}
        selected={invalid ? null : date}
        value={invalid ? (draft && draft.source === field.value ? draft.text : String(field.value)) : props.value}
        onChangeRaw={(event) => {
          if (props.onChangeRaw) props.onChangeRaw(event);
          if (!invalid || !event || (event.isDefaultPrevented && event.isDefaultPrevented()) || !event.target || typeof event.target.value !== "string") return;
          // Keep an unparsed edit local, as react-datepicker does for valid dates.
          // Neither mounting nor parent renders normalize the original Formik value.
          const text = event.target.value;
          setDraft({ source: field.value, text });
          if (text === "") setFieldValue(field.name, "");
        }}
        onChange={(value) => setFieldValue(field.name, dateFromPicker(value, { timeZone, calendarDate }))}
      />

      <FieldFeedback id={feedback.id} error={error} hint={hint} />
    </li>
  );
};

export default AADatepicker;
